import API from '@services/API';
import { API_STATUS } from '@constants/common.constants';
import { AnyAction, Dispatch } from 'redux';

import { ROOT_STATE } from '@store/store.types';

import { setError, startSpinner, stopSpinner } from './actionCreators';
import type { IPaginatedFetchRequest } from './paginatedFetch.types';

const spinnerActions = { setError, startSpinner, stopSpinner };

/**
 * Generic factory for paginated, sortable list GETs (member or CAP).
 * Query: pageNumber, pageSize, sortColumn, sortOrder, plus optional params via buildQueryParams.
 */
export function createPaginatedFetchThunk<
    TRequest extends IPaginatedFetchRequest,
    TPageData extends object,
    TListKey extends keyof TPageData,
    TPayload,
>(config: {
    getEndpointUrl: () => string;
    listKey: TListKey;
    setList: (payload: TPayload) => AnyAction;
    requestDefaults: TRequest;
    buildEmptyPayload: (request: TRequest) => TPayload;
    buildErrorPayload: (request: TRequest) => TPayload;
    buildSuccessPayload: (pageData: TPageData, request: TRequest) => TPayload;
    buildQueryParams?: (request: TRequest) => Record<string, string | number | boolean | undefined>;
}) {
    const {
        getEndpointUrl,
        listKey,
        setList,
        requestDefaults,
        buildEmptyPayload,
        buildErrorPayload,
        buildSuccessPayload,
        buildQueryParams,
    } = config;

    return (options: Partial<TRequest> = {}) => {
        return async (dispatch: Dispatch<AnyAction>, getState: () => ROOT_STATE) => {
            const request = { ...requestDefaults, ...options };
            const baseUrl = getEndpointUrl();
            const params = new URLSearchParams();
            params.set('pageNumber', String(request.pageNumber));
            params.set('pageSize', String(request.pageSize));
            params.set('sortColumn', String(request.sortColumn));
            params.set('sortOrder', String(request.sortOrder));

            if (buildQueryParams) {
                const extraParams = buildQueryParams(request);
                Object.entries(extraParams).forEach(([key, value]) => {
                    if (value !== undefined && value !== null) {
                        params.set(key, String(value));
                    }
                });
            }
            const url = `${baseUrl}?${params.toString()}`;
            try {
                const res = await API.getData(url, dispatch, spinnerActions, true, getState());
                const isSuccess = res?.resultType === API_STATUS.SUCCESS.toUpperCase();

                if (!isSuccess) {
                    dispatch(setList(buildErrorPayload(request)));
                    return null;
                }
                const pageData = res.data as TPageData;

                const list = pageData[listKey];

                if (!Array.isArray(list)) {
                    dispatch(setList(buildEmptyPayload(request)));
                    return null;
                }

                dispatch(setList(buildSuccessPayload(pageData, request)));
                return res;
            } catch {
                dispatch(setList(buildErrorPayload(request)));
                return null;
            }
        };
    };
}
